import {
    segmentTrackSetSensitivities,
    SetSensitivitiesMethod
} from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { ProjectType } from "in_queue/types/projectType";
import { MisoPercentageReduction } from "in_queue/types/scenarioType";
import { useEffect, useRef } from "react";
import { normalizePriorQueueEris } from "../ScenarioDialog/priorQueueAssumptions";
import { applyProjectAssumptionChange } from "./projectAssumptionEdit";

export interface ProjectReductionChange {
    readonly projectId: string;
    readonly projectType: ProjectType;
    readonly eris?: number;
    readonly nris?: number;
}

export interface ProjectAssumptionsEditor {
    readonly setReduction: (change: ProjectReductionChange) => void;
    readonly setReductions: (
        changes: ReadonlyArray<ProjectReductionChange>
    ) => void;
    readonly setIncluded: (params: {
        readonly projectId: string;
        readonly projectType: ProjectType;
        readonly included: boolean;
    }) => void;
}

export const useProjectAssumptionsEditor = ({
    method,
    projectSizeAssumptions,
    setProjectSizeAssumptions,
    includedSpeculativeProjectIds,
    setIncludedSpeculativeProjectIds,
    capacityOnlyProjectIds
}: {
    readonly method: SetSensitivitiesMethod;
    readonly projectSizeAssumptions: Record<string, MisoPercentageReduction>;
    readonly setProjectSizeAssumptions: (
        assumptions: Record<string, MisoPercentageReduction>
    ) => void;
    readonly includedSpeculativeProjectIds: ReadonlyArray<string>;
    readonly setIncludedSpeculativeProjectIds: (
        ids: ReadonlyArray<string>
    ) => void;
    readonly capacityOnlyProjectIds: ReadonlySet<string>;
}): ProjectAssumptionsEditor => {
    const cluster = useCluster();
    const selectionRef = useRef({
        projectSizeAssumptions,
        includedSpeculativeProjectIds
    });
    selectionRef.current = {
        projectSizeAssumptions,
        includedSpeculativeProjectIds
    };
    const changedProjectIdsRef = useRef<Set<string>>(new Set());
    const clusterRef = useRef(cluster);
    clusterRef.current = cluster;
    const methodRef = useRef(method);
    methodRef.current = method;

    useEffect(
        () => () => {
            const changedIds = changedProjectIdsRef.current;
            if (changedIds.size > 0) {
                segmentTrackSetSensitivities({
                    method: methodRef.current,
                    numProjectsAffected: changedIds.size,
                    cluster: clusterRef.current
                });
            }
        },
        []
    );

    // PQ energy is withdraw-only (0 or 100). Normalize any stale partial ERIS -- e.g. from a CSV
    // upload or a pre-feature saved scenario -- to a full 100% withdrawal so the persisted state
    // matches the UI.
    useEffect(() => {
        const { assumptions, normalizedProjectIds } = normalizePriorQueueEris(
            projectSizeAssumptions,
            new Set(capacityOnlyProjectIds)
        );
        if (normalizedProjectIds.length > 0) {
            setProjectSizeAssumptions(assumptions);
        }
    }, [
        projectSizeAssumptions,
        capacityOnlyProjectIds,
        setProjectSizeAssumptions
    ]);

    const setReductions = (changes: ReadonlyArray<ProjectReductionChange>) => {
        let next = selectionRef.current;
        for (const { projectId, projectType, eris, nris } of changes) {
            const current = next.projectSizeAssumptions[projectId];
            next = applyProjectAssumptionChange({
                selection: next,
                projectId,
                projectType,
                eris: eris ?? current?.eris ?? 0,
                nris: nris ?? current?.nris ?? 0
            });
            changedProjectIdsRef.current.add(projectId);
        }
        setIncludedSpeculativeProjectIds(next.includedSpeculativeProjectIds);
        setProjectSizeAssumptions(next.projectSizeAssumptions);
    };
    const setReduction = (change: ProjectReductionChange) =>
        setReductions([change]);

    return {
        setReduction,
        setReductions,
        setIncluded: ({ projectId, projectType, included }) =>
            setReduction({
                projectId,
                projectType,
                eris: included ? 0 : 100,
                nris: included ? 0 : 100
            })
    };
};
